import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import type { VehicleCheck } from '@/types/models';

export interface OpenDefects {
  count: number;
  names: string[];
  day: string;
}

/**
 * Defects on a vehicle's latest mirrored check (0057). Shown on the fleet
 * register; a later clear check replaces the flag. Do not hide a defect
 * merely because it has become a week old.
 */
export function useOpenDefects(): Map<string, OpenDefects> {
  return useLiveQuery(async () => {
    const recent = await db.vehicle_checks.toArray();
    const latest = new Map<string, VehicleCheck>();
    for (const c of recent) {
      if (c.deleted_at) continue;
      const p = latest.get(c.machine_id);
      if (!p || c.check_date > p.check_date) latest.set(c.machine_id, c);
    }
    const bad = [...latest.values()].filter((c) => c.defects > 0);
    const out = new Map<string, OpenDefects>();
    if (!bad.length) return out;
    const rows = await db.vehicle_check_items.where('check_id').anyOf(bad.map((c) => c.id)).toArray();
    const names = new Map((await db.check_items.toArray()).map((i) => [i.id, i.name]));
    for (const c of bad) {
      out.set(c.machine_id, {
        count: c.defects,
        day: c.check_date,
        names: rows.filter((r) => r.check_id === c.id && r.result === 'defect')
          .map((r) => (r.note ? `${names.get(r.item_id) ?? 'Item'}: ${r.note}` : names.get(r.item_id) ?? 'Item')),
      });
    }
    return out;
  }, [], new Map<string, OpenDefects>());
}

/** The chip's hover text. */
export const defectTitle = (d: OpenDefects) => `Check of ${d.day}: ${d.names.join('; ')}`;
